import React, { useState, useCallback, useMemo } from 'react';
import {
    Box, Title, Text, Paper, SimpleGrid, Button, Accordion, Badge, Alert,
    Loader, Table,
    Group, Stack
} from '@mantine/core';
import {
    IconChevronDown,
    IconPlayerPlay,
    IconCircleCheck,
    IconX,
    IconInfoCircle,
    IconCode,
    IconLetterCase,
    IconSearch,
    IconArrowsExchange
} from '@tabler/icons-react';
import { humanize, getSingularLabel, getPluralizedLabel } from '../../utils/pluralizationUtils';
import { generateSimpleSearchFilter, generateSimpleSearchFilterWithEntityType, escapeSearchTerm } from '../../utils/scimSearchUtils';
import { formatDateTime } from '../../utils/dateTimeUtils';

// Test result interfaces
interface TestResult {
    id: string;
    name: string;
    status: 'pending' | 'running' | 'passed' | 'failed' | 'skipped';
    duration?: number;
    error?: string;
    result?: any;
    timestamp?: string;
    expected?: any;
    actual?: any;
}

interface TestCategory {
    id: string;
    name: string;
    description: string;
    icon: React.ReactNode;
    tests: TestDefinition[];
}

interface TestDefinition {
    id: string;
    name: string;
    description: string;
    category: string;
    runTest: () => Promise<{ passed: boolean; result?: any; expected?: any; actual?: any; error?: string }>;
}

export const JsTesting: React.FC = () => {
    const [testResults, setTestResults] = useState<TestResult[]>([]);
    const [runningTests, setRunningTests] = useState<Set<string>>(new Set());
    const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

    // Test categories with comprehensive test definitions
    const testCategories: TestCategory[] = useMemo(() => [
        {
            id: 'humanization',
            name: 'Humanization Utilities',
            description: 'Tests for the humanize function that converts PascalCase/camelCase to human-readable format',
            icon: <IconLetterCase size={20} />,
            tests: [
                {
                    id: 'humanize-pascal-case',
                    name: 'PascalCase Humanization',
                    description: 'Test humanize function with PascalCase strings',
                    category: 'humanization',
                    runTest: async () => {
                        const testCases = [
                            { input: 'XmlFunction', expected: 'Xml Function' },
                            { input: 'StudentTransportationNeed', expected: 'Student Transportation Need' },
                            { input: 'WeekdayAccess', expected: 'Weekday Access' },
                            { input: 'WeekendAccess', expected: 'Weekend Access' },
                            { input: 'PerpetualAccess', expected: 'Perpetual Access' },
                            { input: 'ResponsibilityCenter', expected: 'Responsibility Center' },
                            { input: 'ApiUser', expected: 'Api User' },
                            { input: 'RcJobCode', expected: 'Rc Job Code' }
                        ];

                        const results = testCases.map(({ input, expected }) => {
                            const actual = humanize(input);
                            return {
                                input,
                                expected,
                                actual,
                                passed: actual === expected
                            };
                        });

                        const allPassed = results.every(r => r.passed);
                        return {
                            passed: allPassed,
                            result: results,
                            expected: testCases.map(t => t.expected),
                            actual: results.map(r => r.actual)
                        };
                    }
                },
                {
                    id: 'humanize-camel-case',
                    name: 'camelCase Humanization',
                    description: 'Test humanize function with camelCase strings',
                    category: 'humanization',
                    runTest: async () => {
                        const testCases = [
                            { input: 'xmlFunction', expected: 'xml Function' },
                            { input: 'studentTransportationNeed', expected: 'student Transportation Need' },
                            { input: 'weekdayAccess', expected: 'weekday Access' },
                            { input: 'apiUser', expected: 'api User' }
                        ];

                        const results = testCases.map(({ input, expected }) => {
                            const actual = humanize(input);
                            return {
                                input,
                                expected,
                                actual,
                                passed: actual === expected
                            };
                        });

                        const allPassed = results.every(r => r.passed);
                        return {
                            passed: allPassed,
                            result: results,
                            expected: testCases.map(t => t.expected),
                            actual: results.map(r => r.actual)
                        };
                    }
                },
                {
                    id: 'humanize-edge-cases',
                    name: 'Edge Cases',
                    description: 'Test humanize function with edge cases (empty strings, single words, etc.)',
                    category: 'humanization',
                    runTest: async () => {
                        const testCases = [
                            { input: '', expected: '' },
                            { input: 'Single', expected: 'Single' },
                            { input: 'xml', expected: 'xml' },
                            { input: 'xmlFunction', expected: 'xml Function' },
                            { input: 'XmlFunction', expected: 'Xml Function' }
                        ];

                        const results = testCases.map(({ input, expected }) => {
                            const actual = humanize(input);
                            return {
                                input,
                                expected,
                                actual,
                                passed: actual === expected
                            };
                        });

                        const allPassed = results.every(r => r.passed);
                        return {
                            passed: allPassed,
                            result: results,
                            expected: testCases.map(t => t.expected),
                            actual: results.map(r => r.actual)
                        };
                    }
                }
            ]
        },
        {
            id: 'pluralization',
            name: 'Pluralization Utilities',
            description: 'Tests for getSingularLabel and getPluralizedLabel functions',
            icon: <IconArrowsExchange size={20} />,
            tests: [
                {
                    id: 'singular-label',
                    name: 'Get Singular Label',
                    description: 'Test getSingularLabel function with various entity types',
                    category: 'pluralization',
                    runTest: async () => {
                        const testCases = [
                            { input: 'Employee', expected: 'Employee' },
                            { input: 'Employees', expected: 'Employee' },
                            { input: 'Student', expected: 'Student' },
                            { input: 'Students', expected: 'Student' },
                            { input: 'StudentTransportationNeed', expected: 'Student Transportation Need' },
                            { input: 'StudentTransportationNeeds', expected: 'Student Transportation Need' },
                            { input: 'ResponsibilityCenter', expected: 'Responsibility Center' },
                            { input: 'ResponsibilityCenters', expected: 'Responsibility Center' },
                            { input: 'WeekdayAccess', expected: 'Weekday Access' },
                            { input: 'WeekendAccess', expected: 'Weekend Access' },
                            { input: 'PerpetualAccess', expected: 'Perpetual Access' }
                        ];

                        const results = testCases.map(({ input, expected }) => {
                            const actual = getSingularLabel(input);
                            return {
                                input,
                                expected,
                                actual,
                                passed: actual === expected
                            };
                        });

                        const allPassed = results.every(r => r.passed);
                        return {
                            passed: allPassed,
                            result: results,
                            expected: testCases.map(t => t.expected),
                            actual: results.map(r => r.actual)
                        };
                    }
                },
                {
                    id: 'plural-label',
                    name: 'Get Pluralized Label',
                    description: 'Test getPluralizedLabel function with various entity types',
                    category: 'pluralization',
                    runTest: async () => {
                        const testCases = [
                            { input: 'Employee', expected: 'Employees' },
                            { input: 'Student', expected: 'Students' },
                            { input: 'Class', expected: 'Classes' },
                            { input: 'StudentTransportationNeed', expected: 'Student Transportation Needs' },
                            { input: 'ResponsibilityCenter', expected: 'Responsibility Centers' },
                            { input: 'WeekdayAccess', expected: 'Weekday Access' },
                            { input: 'WeekendAccess', expected: 'Weekend Access' },
                            { input: 'PerpetualAccess', expected: 'Perpetual Access' }
                        ];

                        const results = testCases.map(({ input, expected }) => {
                            const actual = getPluralizedLabel(input);
                            return {
                                input,
                                expected,
                                actual,
                                passed: actual === expected
                            };
                        });

                        const allPassed = results.every(r => r.passed);
                        return {
                            passed: allPassed,
                            result: results,
                            expected: testCases.map(t => t.expected),
                            actual: results.map(r => r.actual)
                        };
                    }
                }
            ]
        },
        {
            id: 'scim-search',
            name: 'SCIM Search Utilities',
            description: 'Tests for SCIM filter generation and search term escaping',
            icon: <IconSearch size={20} />,
            tests: [
                {
                    id: 'escape-search-term',
                    name: 'Search Term Escaping',
                    description: 'Test escapeSearchTerm function with special characters',
                    category: 'scim-search',
                    runTest: async () => {
                        const testCases = [
                            { input: 'normal', expected: 'normal' },
                            { input: 'with"quote', expected: 'with\\"quote' },
                            { input: 'with\\backslash', expected: 'with\\\\backslash' },
                            { input: 'with"both\\', expected: 'with\\"both\\\\' },
                            { input: 'test\\"quote', expected: 'test\\\\\\"quote' }
                        ];

                        const results = testCases.map(({ input, expected }) => {
                            const actual = escapeSearchTerm(input);
                            return {
                                input,
                                expected,
                                actual,
                                passed: actual === expected
                            };
                        });

                        const allPassed = results.every(r => r.passed);
                        return {
                            passed: allPassed,
                            result: results,
                            expected: testCases.map(t => t.expected),
                            actual: results.map(r => r.actual)
                        };
                    }
                },
                {
                    id: 'simple-search-filter',
                    name: 'Simple Search Filter Generation',
                    description: 'Test generateSimpleSearchFilter function',
                    category: 'scim-search',
                    runTest: async () => {
                        const testCases = [
                            { input: 'test', expected: 'DisplayName co "test" or Id co "test"' },
                            { input: 'test"quote', expected: 'DisplayName co "test\\"quote" or Id co "test\\"quote"' },
                            { input: '', expected: undefined },
                            { input: '   ', expected: undefined },
                            { input: '  test  ', expected: 'DisplayName co "test" or Id co "test"' }
                        ];

                        const results = testCases.map(({ input, expected }) => {
                            const actual = generateSimpleSearchFilter(input);
                            return {
                                input,
                                expected,
                                actual,
                                passed: actual === expected
                            };
                        });

                        const allPassed = results.every(r => r.passed);
                        return {
                            passed: allPassed,
                            result: results,
                            expected: testCases.map(t => t.expected),
                            actual: results.map(r => r.actual)
                        };
                    }
                },
                {
                    id: 'entity-type-search-filter',
                    name: 'Entity Type Search Filter Generation',
                    description: 'Test generateSimpleSearchFilterWithEntityType function',
                    category: 'scim-search',
                    runTest: async () => {
                        const testCases = [
                            {
                                input: { searchTerm: 'test', entityType: 'Student' },
                                expected: 'EntityType eq "Student" and (DisplayName co "test" or Id co "test")'
                            },
                            {
                                input: { searchTerm: '', entityType: 'Student' },
                                expected: 'EntityType eq "Student"'
                            },
                            {
                                input: { searchTerm: '  ', entityType: 'Employee' },
                                expected: 'EntityType eq "Employee"'
                            }
                        ];

                        const results = testCases.map(({ input, expected }) => {
                            const actual = generateSimpleSearchFilterWithEntityType(input.searchTerm, input.entityType);
                            return {
                                input,
                                expected,
                                actual,
                                passed: actual === expected
                            };
                        });

                        const allPassed = results.every(r => r.passed);
                        return {
                            passed: allPassed,
                            result: results,
                            expected: testCases.map(t => t.expected),
                            actual: results.map(r => r.actual)
                        };
                    }
                }
            ]
        }
    ], []);

    // Update test result
    const updateTestResult = useCallback((testId: string, result: TestResult) => {
        setTestResults(prev => {
            const existing = prev.findIndex(r => r.id === testId);
            if (existing >= 0) {
                const updated = [...prev];
                updated[existing] = result;
                return updated;
            }
            return [...prev, result];
        });
    }, []);

    // Run a single test
    const runTest = useCallback(async (test: TestDefinition) => {
        const testId = `${test.category}-${test.id}`;

        setRunningTests(prev => new Set(prev).add(testId));
        updateTestResult(testId, {
            id: testId,
            name: test.name,
            status: 'running',
            timestamp: new Date().toISOString()
        });

        const startTime = Date.now();

        try {
            const testResult = await test.runTest();
            const duration = Date.now() - startTime;

            updateTestResult(testId, {
                id: testId,
                name: test.name,
                status: testResult.passed ? 'passed' : 'failed',
                duration,
                result: testResult.result,
                expected: testResult.expected,
                actual: testResult.actual,
                error: testResult.error,
                timestamp: new Date().toISOString()
            });
        } catch (error) {
            const duration = Date.now() - startTime;
            updateTestResult(testId, {
                id: testId,
                name: test.name,
                status: 'failed',
                duration,
                error: error instanceof Error ? error.message : 'Unknown error',
                timestamp: new Date().toISOString()
            });
        } finally {
            setRunningTests(prev => {
                const updated = new Set(prev);
                updated.delete(testId);
                return updated;
            });
        }
    }, [updateTestResult]);

    // Run all tests in a category
    const runCategoryTests = useCallback(async (category: TestCategory) => {
        for (const test of category.tests) {
            await runTest(test);
        }
    }, [runTest]);

    // Run all tests
    const runAllTests = useCallback(async () => {
        setTestResults([]);
        setRunningTests(new Set());

        for (const category of testCategories) {
            await runCategoryTests(category);
        }
    }, [testCategories, runCategoryTests]);

    // Clear all results
    const clearResults = useCallback(() => {
        setTestResults([]);
        setRunningTests(new Set());
    }, []);

    // Get test result
    const getTestResult = (test: TestDefinition): TestResult | undefined => {
        const testId = `${test.category}-${test.id}`;
        return testResults.find(r => r.id === testId);
    };

    // Get status icon
    const getStatusIcon = (status: TestResult['status']) => {
        switch (status) {
            case 'running':
                return <Loader size={16} />;
            case 'passed':
                return <IconCircleCheck size={16} color="var(--mantine-color-green-6)" />;
            case 'failed':
                return <IconX size={16} color="var(--mantine-color-red-6)" />;
            default:
                return <IconInfoCircle size={16} color="var(--mantine-color-dimmed)" />;
        }
    };

    return (
        <Box p="lg">
            <Title order={1} mb="xs">
                JavaScript Utilities Testing
            </Title>
            <Text size="sm" c="dimmed" mb="lg">
                Test and validate JavaScript utility functions used throughout the application
            </Text>

            {/* Control Panel */}
            <Paper p="md" mb="lg" withBorder>
                <Group gap="md" wrap="wrap" align="center">
                    <Button
                        leftSection={<IconPlayerPlay size={18} />}
                        onClick={runAllTests}
                        disabled={runningTests.size > 0}
                    >
                        Run All Tests
                    </Button>
                    <Button
                        variant="outline"
                        leftSection={<IconX size={18} />}
                        onClick={clearResults}
                        disabled={runningTests.size > 0}
                    >
                        Clear Results
                    </Button>
                    <Text size="sm" c="dimmed">
                        {testResults.filter(r => r.status === 'passed').length} / {testResults.length} tests passed
                    </Text>
                </Group>
            </Paper>

            {/* Test Categories */}
            {testCategories.map(category => (
                <Accordion
                    key={category.id}
                    value={expandedCategory}
                    onChange={setExpandedCategory}
                    mb="md"
                >
                    <Accordion.Item value={category.id}>
                        <Accordion.Control>
                            <Group gap="md" wrap="nowrap">
                                {category.icon}
                                <Box flex={1}>
                                    <Title order={5}>{category.name}</Title>
                                    <Text size="sm" c="dimmed">
                                        {category.description}
                                    </Text>
                                </Box>
                                <Button
                                    variant="outline"
                                    size="xs"
                                    leftSection={<IconPlayerPlay size={14} />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        runCategoryTests(category);
                                    }}
                                    disabled={runningTests.size > 0}
                                >
                                    Run All {category.name} Tests
                                </Button>
                            </Group>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <SimpleGrid cols={{ base: 1 }}>
                                {category.tests.map(test => {
                                    const result = getTestResult(test);
                                    const testId = `${test.category}-${test.id}`;
                                    const isRunning = runningTests.has(testId);

                                    return (
                                        <Paper key={test.id} withBorder p="md">
                                            <Group gap="md" mb="xs">
                                                {result ? getStatusIcon(result.status) : <IconInfoCircle size={16} color="var(--mantine-color-dimmed)" />}
                                                <Title order={5}>{test.name}</Title>
                                                {result?.duration && (
                                                    <Badge variant="outline" size="sm">
                                                        {result.duration}ms
                                                    </Badge>
                                                )}
                                            </Group>
                                            <Text size="sm" c="dimmed" mb="md">
                                                {test.description}
                                            </Text>

                                            {result && (
                                                <Stack gap="xs">
                                                    {result.status === 'failed' && result.error && (
                                                        <Alert color="red">
                                                            {result.error}
                                                        </Alert>
                                                    )}
                                                    {result.expected && result.actual && (
                                                        <Box>
                                                            <Text size="xs" c="dimmed">
                                                                Expected: {JSON.stringify(result.expected)}
                                                            </Text>
                                                            <Text size="xs" c="dimmed">
                                                                Actual: {JSON.stringify(result.actual)}
                                                            </Text>
                                                        </Box>
                                                    )}
                                                    {result.result && (
                                                        <Paper p="md" withBorder mt="xs">
                                                            <Text size="xs" c="dimmed" mb="xs" component="div">
                                                                Test Results:
                                                            </Text>
                                                            <Box component="pre" style={{ margin: 0, fontSize: 12, overflow: 'auto' }}>
                                                                {JSON.stringify(result.result, null, 2)}
                                                            </Box>
                                                        </Paper>
                                                    )}
                                                </Stack>
                                            )}
                                            <Group mt="md">
                                                <Button
                                                    size="xs"
                                                    loading={isRunning}
                                                    leftSection={<IconPlayerPlay size={16} />}
                                                    onClick={() => runTest(test)}
                                                >
                                                    {isRunning ? 'Running...' : 'Run Test'}
                                                </Button>
                                            </Group>
                                        </Paper>
                                    );
                                })}
                            </SimpleGrid>
                        </Accordion.Panel>
                    </Accordion.Item>
                </Accordion>
            ))}

            {/* Results Summary Table */}
            {testResults.length > 0 && (
                <Paper p="md" mt="lg" withBorder>
                    <Title order={5} mb="md">
                        Test Results Summary
                    </Title>
                    <Table>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Test Name</Table.Th>
                                <Table.Th>Status</Table.Th>
                                <Table.Th>Duration</Table.Th>
                                <Table.Th>Timestamp</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {testResults.map(result => (
                                <Table.Tr key={result.id}>
                                    <Table.Td>{result.name}</Table.Td>
                                    <Table.Td>
                                        {getStatusIcon(result.status)}
                                    </Table.Td>
                                    <Table.Td>{result.duration ? `${result.duration}ms` : '-'}</Table.Td>
                                    <Table.Td>
                                        {result.timestamp ? formatDateTime(result.timestamp) : '-'}
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Paper>
            )}
        </Box>
    );
};
